import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import { Body } from 'js/design_system/Body';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Icon } from 'js/design_system/Icon';
import CloseIcon from 'js/design_system/icons/close.svg';
import {
  DeprecatedGrid,
  GridColumn,
} from 'js/design_system/grid/DeprecatedGrid';
import getResponsivenessTraits, {
  PageName,
} from 'js/header/responsivenessTraits';
import SearchBar from 'js/header/SearchBar/SearchBar';
import { currentUser } from 'js/utils/user';
import { get } from 'js/utils/api_utils';
import useScreenSize from 'js/utils/hooks/useScreenSize';
import useScrollLock from 'js/utils/hooks/useScrollLock';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

import AcademiaIcon from './AcademiaIcon';
import AccountMenu from './AccountMenu';
import AccountMenuButton from './AccountMenuButton';
import SmallAccountMenuButton from './SmallAccountMenuButton';
import ToolsMenu from './ToolsMenu';
import ToolsSidebar from './ToolsSidebar';
import {
  grantsMarketingIconButton,
  GrantsMarketingButton,
} from './GrantsMarketingButton';
import ImpactNavButton from './ImpactNavButton';
import PodcastLibraryButton from './PodcastLibraryButton';
import PremiumUpsellButton from './PremiumUpsellButton';
import RadarNavButton from './RadarNavButton';
import UploadButton from './UploadButton';
import {
  IconAnchor,
  IconButton,
  IconContainer,
  IconLabel,
  NotificationDot,
} from './buttonStyles';
import { setDocumentTitleCount } from './documentTitleCount';
import { useFocusedNavDynamicProps } from './useFocusedNavDynamicProps';

// The nav switches to the mobile style below this value (equal to $ds-tablet).
// If changing this value must also update '.focused-nav-container' styles.
export const HAMBURGER_MENU_SIZE = 768; // px
const NAV_HEIGHT_DESKTOP = 80;
export const NAV_HEIGHT_MOBILE = 56;

const NavAndTools = styled.div<{ isMobileNav: boolean }>`
  position: relative;
  z-index: 10;
  height: ${NAV_HEIGHT_DESKTOP}px;
  &.isMobileNav {
    height: ${NAV_HEIGHT_MOBILE}px;
  }
`;

const NavContainer = styled.div<{ inDevStyle?: boolean; useGrid?: boolean }>`
  max-height: ${NAV_HEIGHT_DESKTOP}px;
  max-width: 100vw;
  background-color: #ffffff;

  &.inDevStyle {
    background-color: aqua;
  }

  padding: 16px;
  &.useGrid {
    padding: 16px 0;
  }
`;

const MobileNavContainer = styled(NavContainer)<{ useGrid?: boolean }>`
  padding: 7px 12px;
  &.useGrid {
    padding: 7px 0;
  }
`;

const MobileSearchContainer = styled.div`
  flex-grow: 1;
  min-width: 0;
`;

const MobileUpsellContainer = styled.div`
  flex-shrink: 1;
  min-width: 0;
  max-width: 40%;
  overflow: hidden;
`;

const WidthContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;

  > * {
    margin: 0;
    white-space: nowrap;
    > * {
      white-space: initial;
    }
  }
  > * ~ * {
    margin: 0 0 0 16px;
  }

  @media (max-width: ${HAMBURGER_MENU_SIZE}px) {
    > * ~ * {
      margin: 0 0 0 8px;
    }
  }
`;

const SearchBarContainer = styled.div`
  flex: 1 1 auto;
`;

const IconAnchorsContainer = styled.div`
  flex: 0 1 auto;
  display: flex;
  justify-content: space-between;
  max-width: 380px;

  > * {
    margin: 0 8px;
  }
`;

const FullViewMenu = styled.div`
  position: fixed;
  top: calc(var(--nav-mobile-height) + 2px);
  left: 0;
  width: 100vw;
  height: calc(100svh - var(--nav-mobile-height) - 2px);
  overflow-y: scroll;
  overflow-x: hidden;
  background: white;
  padding: 16px;
  padding-bottom: 64px;
`;

const CloseMenuButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  margin: 0;
  padding: 12px 16px;
  border: none;
  background: transparent;
`;

const BottomMenu = styled.div`
  box-shadow: 0px -2px 4px 0px #00000014;
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100vw;
  background: white;
  flex: 1 1 auto;
  display: flex;
  justify-content: space-evenly;
  height: 48px;

  @media (max-width: 300px) {
    > *:nth-child(3) {
      display: none;
    }
  }
`;

const RestrictedUserBannerContainer = styled(Body)`
  width: 100%;
  padding: 12px;
  background-color: var(--brand-colors-peach);
  color: var(--text-primary);
  text-align: center;
`;

const RestrictedUserBannerLink = styled(BodyLink)`
  margin-left: 8px;
`;

type RestrictedUserBannerProps = {
  restrictedUser?: RestrictedUserType;
  confirmAccountEmail?: string;
};

const RestrictedUserBanner = ({
  restrictedUser,
  confirmAccountEmail,
}: RestrictedUserBannerProps) => {
  if (!restrictedUser) {
    return null;
  }

  switch (restrictedUser) {
    case 'unconfirmed':
      return (
        <RestrictedUserBannerContainer>
          Please confirm your account. We&apos;ve sent a confirmation link to{' '}
          {confirmAccountEmail}.
          <RestrictedUserBannerLink underline href="/confirm/send_new_email">
            Resend
          </RestrictedUserBannerLink>
          <RestrictedUserBannerLink underline href="/confirm/sent#email">
            Change Email
          </RestrictedUserBannerLink>
        </RestrictedUserBannerContainer>
      );
    case 'flagged':
      return (
        <RestrictedUserBannerContainer>
          Your account was flagged as suspicious by an automated system. If this
          was an error, please accept our apologies.
          <RestrictedUserBannerLink underline href="/user_unflag_requests/new">
            Request Unflagging
          </RestrictedUserBannerLink>
          <RestrictedUserBannerLink underline href="/settings">
            Account Settings
          </RestrictedUserBannerLink>
        </RestrictedUserBannerContainer>
      );
    case 'flagged_pending':
      return (
        <RestrictedUserBannerContainer>
          Your account was flagged as suspicious, and your reconsideration
          request is awaiting manual review. We&apos;re sorry for any
          inconvenience.
          <RestrictedUserBannerLink underline href="/settings">
            Account Settings
          </RestrictedUserBannerLink>
        </RestrictedUserBannerContainer>
      );
    default: {
      const exhaustiveCheck: never = restrictedUser;
      throw new Error(`Unhandled restricted user type: ${exhaustiveCheck}`);
    }
  }
};

type ResponsiveIcon = {
  icon: ReactNode;
  // Lower priority wins on smaller screens.
  priority: number;
};

type ContainerProps = {
  useGrid: boolean;
  maxWidth: number | undefined;
  children: React.ReactNode;
};

const Container = ({ useGrid, maxWidth, children }: ContainerProps) =>
  useGrid ? (
    <DeprecatedGrid>
      <GridColumn sm={2} md={8} lg={12} xl={12}>
        <WidthContainer>{children}</WidthContainer>
      </GridColumn>
    </DeprecatedGrid>
  ) : (
    <WidthContainer style={{ maxWidth }}>{children}</WidthContainer>
  );

type Menu = 'account' | 'tools' | 'search';

type RestrictedUserType = 'unconfirmed' | 'flagged' | 'flagged_pending';

type FocusedNavProps = {
  inDevStyle?: boolean;
  pageName: PageName;
  useGrid?: boolean;
  initialQuery?: string;
  isPremium?: boolean;
  hidePremiumUpsellButton?: boolean;
  restrictedUser?: RestrictedUserType;
  confirmAccountEmail?: string;
  showCitationsDashboard?: boolean;
};

const FocusedNav = ({
  inDevStyle,
  pageName,
  useGrid = false,
  initialQuery,
  isPremium = false,
  hidePremiumUpsellButton,
  restrictedUser,
  confirmAccountEmail,
  showCitationsDashboard,
}: FocusedNavProps) => {
  // Allow conditional rendering of components that aren't SSR-safe
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const dynamicProps = useFocusedNavDynamicProps();
  const notifications = dynamicProps?.notifications;
  const premiumUpsells = dynamicProps?.premium_upsells;
  const featureFlags = dynamicProps?.feature_flags;

  // Feature flags loaded asynchronously
  const showGrantsButtonOnNav = featureFlags?.showGrantsButtonOnNav ?? false;
  const showImpactHubNav = featureFlags?.showImpactHubNav ?? false;
  const showPodcastLibraryNav = featureFlags?.showPodcastLibraryNav ?? false;
  const showResearchRadarNav = featureFlags?.showResearchRadarNav ?? false;
  const showCitationsDashboardNav = showCitationsDashboard ?? false;

  const [openMenu, setOpenMenu] = useState<null | Menu>(null);
  const { screenWidth } = useScreenSize();
  const { maxWidth } = getResponsivenessTraits(pageName, screenWidth);
  const toolsButtonRef = useRef<HTMLButtonElement>(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [mentionsCount, setMentionsCount] = useState(0);

  const isMobileNav = screenWidth < HAMBURGER_MENU_SIZE;

  // Which icon to show at top of mobile nav
  const mobileTopIcon = useMemo(() => {
    if (restrictedUser) {
      return null;
    }
    if (isPremium) {
      return 'tools_top';
    }
    if (showPodcastLibraryNav) {
      return 'podcast_library_top';
    }
    return 'upsell_top';
  }, [isPremium, restrictedUser, showPodcastLibraryNav]);

  // A button to show next to the menu on desktop
  const desktopHighlightedButton = useMemo(() => {
    if (restrictedUser) {
      return null;
    }
    if (showPodcastLibraryNav) {
      return 'podcast_library';
    }
    if (!isPremium && !hidePremiumUpsellButton) {
      return 'premium_upsell';
    }
    if (isPremium && showGrantsButtonOnNav) {
      return 'grants';
    }
    return null;
  }, [
    hidePremiumUpsellButton,
    isPremium,
    restrictedUser,
    showPodcastLibraryNav,
    showGrantsButtonOnNav,
  ]);

  useEffect(() => {
    const url = routes.unread_count_api_v0_notification_messages_path({
      subdomain_param: 'api',
    });
    get(url).then(({ count }) => setNotificationCount(count));
  }, []);

  useEffect(() => {
    const url = routes.unread_count_api_v0_mentions_path({
      subdomain_param: 'api',
    });
    get(url).then(({ count }) => setMentionsCount(count));
  }, []);

  const [radarUnreadCount, setRadarUnreadCount] = useState(0);
  useEffect(() => {
    if (!isPremium || !showResearchRadarNav) return;
    const url = routes.api_v0_research_radar_unread_count_path({
      subdomain_param: 'api',
    });
    get(url).then(({ unread_count }) => setRadarUnreadCount(unread_count));
  }, [isPremium, showResearchRadarNav]);

  const totalCount =
    notificationCount +
    mentionsCount +
    radarUnreadCount +
    (notifications?.messagesCount ?? 0) +
    (notifications?.hasUnseenReaders ? 1 : 0);

  // SSR-safe: Update document title in useEffect to avoid hydration mismatch
  useEffect(() => {
    setDocumentTitleCount(totalCount);
  }, [totalCount]);

  const toggleMenu = useCallback(
    (menu: Menu): void => {
      if (openMenu === menu) {
        setOpenMenu(null);
      } else {
        setOpenMenu(menu);
      }
    },
    [openMenu],
  );

  const homeButton = useMemo(
    () => (
      <IconAnchor
        key="home"
        href={routes.home_url({ from_navbar: true, trigger: 'nav' })}
      >
        <IconContainer>
          <Icon name="home" size={28} />
        </IconContainer>
        <IconLabel>Home</IconLabel>
      </IconAnchor>
    ),
    [],
  );

  const impactButton = useMemo(() => <ImpactNavButton />, []);

  const mentionsButton = useMemo(() => {
    if (showCitationsDashboardNav) {
      return (
        <ImpressionWatcher key="citations" source="citations.nav">
          <IconAnchor
            href={routes.citations_url({ from_navbar: true, trigger: 'nav' })}
          >
            <IconContainer>
              <Icon name="format_quote" size={28} />
            </IconContainer>
            <IconLabel>
              Citations
              {mentionsCount > 0 && <NotificationDot />}
            </IconLabel>
          </IconAnchor>
        </ImpressionWatcher>
      );
    }

    return (
      <ImpressionWatcher key="mentions" source="mentions.nav">
        <IconAnchor
          href={routes.mentions_url({ from_navbar: true, trigger: 'nav' })}
        >
          <IconContainer>
            <Icon name="format_quote" size={28} />
          </IconContainer>
          <IconLabel>
            Mentions
            {mentionsCount > 0 && <NotificationDot />}
          </IconLabel>
        </IconAnchor>
      </ImpressionWatcher>
    );
  }, [mentionsCount, showCitationsDashboardNav]);

  const analyticsButton = useMemo(
    () =>
      isClient ? (
        <IconAnchor
          key="analytics"
          href={routes.analytics_dashboard_url({
            page_name: currentUser?.page_name,
            subdomain: currentUser?.domain_name,
            from_navbar: true,
            trigger: 'nav',
          })}
        >
          <IconContainer>
            <Icon name="bar_chart" size={28} />
          </IconContainer>
          <IconLabel>Analytics</IconLabel>
        </IconAnchor>
      ) : null,
    [isClient],
  );

  const uploadButton = useMemo(
    () => (
      <UploadButton
        key="upload"
        hasUnseenClaimablePapers={
          notifications?.hasUnseenClaimablePapers ?? false
        }
        hasUnseenUploadableStubs={
          notifications?.hasUnseenUploadableStubs ?? false
        }
      />
    ),
    [
      notifications?.hasUnseenClaimablePapers,
      notifications?.hasUnseenUploadableStubs,
    ],
  );

  const toolsButton = useMemo(
    () => (
      <IconButton
        ref={toolsButtonRef}
        key="tools"
        onClick={() => toggleMenu('tools')}
        aria-pressed={openMenu === 'tools'}
      >
        <IconContainer>
          <Icon name="apps" size={28} />
        </IconContainer>
        <IconLabel>Tools</IconLabel>
      </IconButton>
    ),
    [openMenu, toggleMenu],
  );

  const grantsMultiplierButton = useMemo(() => grantsMarketingIconButton, []);

  const radarDesktopButton = useMemo(
    () => (
      <RadarNavButton
        key="radar"
        hasUnreadResults={radarUnreadCount > 0}
        source="desktop"
      />
    ),
    [radarUnreadCount],
  );

  const radarMobileButton = useMemo(
    () => (
      <RadarNavButton
        key="radar-mobile"
        hasUnreadResults={radarUnreadCount > 0}
        source="mobile"
      />
    ),
    [radarUnreadCount],
  );

  const icons = useMemo(() => {
    const showGrantMultiplier =
      !isMobileNav && !isPremium && showGrantsButtonOnNav;
    const showImpactHub = showImpactHubNav && !showGrantsButtonOnNav;

    const unfilteredIcons: (ResponsiveIcon | false)[] = [
      { icon: homeButton, priority: 5 },
      showImpactHub && { icon: impactButton, priority: 2 },
      { icon: mentionsButton, priority: 0 },
      showGrantMultiplier && { icon: grantsMultiplierButton, priority: 4 },
      { icon: analyticsButton, priority: 1 },
      isPremium &&
        showResearchRadarNav && { icon: radarDesktopButton, priority: 2 },
      { icon: uploadButton, priority: 3 },
      { icon: toolsButton, priority: -Infinity },
    ];
    const icons: ResponsiveIcon[] = unfilteredIcons.filter(
      // Type guard is needed here to convince typescript that there are no false values
      (icon): icon is ResponsiveIcon => !!icon,
    );

    if (isPremium || hidePremiumUpsellButton) {
      return icons.map((icon) => icon.icon);
    }

    // Hide another icon every 50 pixels past the max width.
    const iconsWithPriority = icons.filter(
      (icon) => icon.priority !== undefined,
    ).length;
    const baseMaxWidth = 950; // Arbitrary pixels that respond best
    const maxWidth =
      baseMaxWidth + (premiumUpsells?.reactivatePremiumUpsellType ? 273 : 127); // Premium button sizes
    const maxLevel =
      iconsWithPriority -
      Math.floor((maxWidth - Math.min(screenWidth, maxWidth)) / 50);
    return icons
      .filter((icon) => icon.priority <= maxLevel)
      .map((icon) => icon.icon);
  }, [
    premiumUpsells?.reactivatePremiumUpsellType,
    screenWidth,
    analyticsButton,
    homeButton,
    impactButton,
    mentionsButton,
    radarDesktopButton,
    uploadButton,
    toolsButton,
    hidePremiumUpsellButton,
    isMobileNav,
    grantsMultiplierButton,
    showGrantsButtonOnNav,
    showImpactHubNav,
    showResearchRadarNav,
    isPremium,
  ]);

  useScrollLock(isMobileNav && openMenu !== null);

  const closeMenuButton = (
    <CloseMenuButton onClick={() => setOpenMenu(null)}>
      <CloseIcon color="#222222" />
    </CloseMenuButton>
  );

  if (isMobileNav) {
    return (
      <>
        <NavAndTools isMobileNav={isMobileNav}>
          <MobileNavContainer inDevStyle={inDevStyle} useGrid={useGrid}>
            <Container maxWidth={maxWidth} useGrid={useGrid}>
              <SmallAccountMenuButton
                count={totalCount}
                menuOpen={openMenu === 'account'}
                setMenuOpen={(open) => setOpenMenu(open ? 'account' : null)}
              />
              <MobileSearchContainer style={{ marginLeft: 24 }}>
                <ImpressionWatcher source="focused_nav_search_bar">
                  <SearchBar
                    initialQuery={initialQuery}
                    isPremium={isPremium}
                  />
                </ImpressionWatcher>
              </MobileSearchContainer>
              {mobileTopIcon === 'tools_top' && (
                <div onClick={() => toggleMenu('tools')}>{toolsButton}</div>
              )}
              {mobileTopIcon === 'upsell_top' && (
                <MobileUpsellContainer>
                  <PremiumUpsellButton
                    premiumUpsells={premiumUpsells}
                    isMobileNav={isMobileNav}
                  />
                </MobileUpsellContainer>
              )}
              {mobileTopIcon === 'podcast_library_top' && (
                <PodcastLibraryButton />
              )}
            </Container>
          </MobileNavContainer>
          {openMenu === 'tools' && (
            <FullViewMenu>
              {closeMenuButton}
              <ToolsMenu
                hasUnseenClaimablePapers={
                  notifications?.hasUnseenClaimablePapers ?? false
                }
                hasUnseenUploadableStubs={
                  notifications?.hasUnseenUploadableStubs ?? false
                }
              />
            </FullViewMenu>
          )}
          {openMenu === 'account' && (
            <FullViewMenu>
              {closeMenuButton}
              <AccountMenu
                restrictedUser={Boolean(restrictedUser)}
                halfOffCoupon={premiumUpsells?.halfOffCoupon}
                reactivate={premiumUpsells?.reactivatePremiumUpsellType}
                mentionsCount={mentionsCount}
                messagesCount={notifications?.messagesCount ?? 0}
                hasUnseenReaders={notifications?.hasUnseenReaders ?? false}
                notificationCount={notificationCount}
                showCitationsDashboard={showCitationsDashboardNav}
              />
            </FullViewMenu>
          )}
          {!restrictedUser && (
            <BottomMenu>
              {homeButton}
              {showImpactHubNav && !showGrantsButtonOnNav && impactButton}
              {mentionsButton}
              {analyticsButton}
              {isPremium && showResearchRadarNav && radarMobileButton}
              {mobileTopIcon !== 'tools_top' ? toolsButton : uploadButton}
            </BottomMenu>
          )}
        </NavAndTools>
        <RestrictedUserBanner
          restrictedUser={restrictedUser}
          confirmAccountEmail={confirmAccountEmail}
        />
      </>
    );
  }

  return (
    <>
      <NavAndTools isMobileNav={isMobileNav}>
        <NavContainer inDevStyle={inDevStyle} useGrid={useGrid}>
          <Container maxWidth={maxWidth} useGrid={useGrid}>
            <AcademiaIcon />
            <SearchBarContainer>
              <ImpressionWatcher source="focused_nav_search_bar">
                <SearchBar initialQuery={initialQuery} isPremium={isPremium} />
              </ImpressionWatcher>
            </SearchBarContainer>
            {!restrictedUser && (
              <IconAnchorsContainer>{icons}</IconAnchorsContainer>
            )}
            {desktopHighlightedButton === 'premium_upsell' && (
              <PremiumUpsellButton
                premiumUpsells={premiumUpsells}
                isMobileNav={isMobileNav}
              />
            )}
            {desktopHighlightedButton === 'grants' && <GrantsMarketingButton />}
            {desktopHighlightedButton === 'podcast_library' && (
              <PodcastLibraryButton />
            )}
            <AccountMenuButton
              restrictedUser={Boolean(restrictedUser)}
              halfOffCoupon={premiumUpsells?.halfOffCoupon}
              reactivate={premiumUpsells?.reactivatePremiumUpsellType}
              totalCount={totalCount}
              notificationCount={notificationCount}
              mentionsCount={mentionsCount}
              messagesCount={notifications?.messagesCount ?? 0}
              hasUnseenReaders={notifications?.hasUnseenReaders ?? false}
              showCitationsDashboard={showCitationsDashboardNav}
              open={openMenu === 'account'}
              setOpen={(open) => setOpenMenu(open ? 'account' : null)}
            />
          </Container>
        </NavContainer>

        {openMenu === 'tools' && (
          <ToolsSidebar
            buttonRef={toolsButtonRef}
            close={() => setOpenMenu(null)}
            hasUnseenClaimablePapers={
              notifications?.hasUnseenClaimablePapers ?? false
            }
            hasUnseenUploadableStubs={
              notifications?.hasUnseenUploadableStubs ?? false
            }
          />
        )}
      </NavAndTools>
      <RestrictedUserBanner
        restrictedUser={restrictedUser}
        confirmAccountEmail={confirmAccountEmail}
      />
    </>
  );
};

export default FocusedNav;
